import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import axios, {CancelToken} from 'axios';
import useSavedComponentsState from '../../../../../../../hooks/use-saved-components-state';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';

/**
 * Compact (K/M prefixes) vs precise (full grouped numbers) display for dashboard widgets.
 */
export const NumberVisibilityModes = {
    compact: 'compact',
    precise: 'precise',
} as const;

export type NumberVisibilityMode = typeof NumberVisibilityModes[keyof typeof NumberVisibilityModes];

const NUMBER_VISIBILITY_SAVED_STATE = {
    name: 'dashboard-v2-number-visibility-mode',
    version: '1.0.0',
} as const;

export const DEFAULT_NUMBER_VISIBILITY_MODE: NumberVisibilityMode = NumberVisibilityModes.compact;

/**
 * Returns whether the stored value is a known number visibility mode.
 */
const isNumberVisibilityMode = (value: unknown): value is NumberVisibilityMode =>
    value === NumberVisibilityModes.compact || value === NumberVisibilityModes.precise;

interface NumberVisibilityModeContextValue {
    mode: NumberVisibilityMode;
    setMode: (mode: NumberVisibilityMode) => void;
    loading: boolean;
}

const defaultContextValue: NumberVisibilityModeContextValue = {
    mode: DEFAULT_NUMBER_VISIBILITY_MODE,
    setMode: () => undefined,
    loading: false,
};

const NumberVisibilityModeContext = createContext<NumberVisibilityModeContextValue | undefined>(undefined);

/**
 * Loads and persists the dashboard number visibility mode for the current page tree.
 */
export const NumberVisibilityModeProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const isMounted = useIsMounted();
    const [mode, setModeState] = useState<NumberVisibilityMode>(DEFAULT_NUMBER_VISIBILITY_MODE);
    const [loading, setLoading] = useState(true);
    const {highestVersion: getCachedMode, save: saveCachedMode} = useSavedComponentsState<NumberVisibilityMode>({
        name: NUMBER_VISIBILITY_SAVED_STATE.name,
        version: NUMBER_VISIBILITY_SAVED_STATE.version,
    });

    /**
     * Loads the persisted number visibility mode.
     */
    const loadCachedMode = useCallback(async (cancelToken?: CancelToken) => {
        setLoading(true);
        const cached = await getCachedMode(cancelToken);
        if (!isMounted() || cancelToken?.reason) {
            return;
        }
        if (isNumberVisibilityMode(cached?.data)) {
            setModeState(cached.data);
        }
        setLoading(false);
    }, [getCachedMode, isMounted]);

    /**
     * Loads the cached mode on mount and cancels in-flight requests on unmount.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void loadCachedMode(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [loadCachedMode]);

    /**
     * Updates the number visibility mode and persists it.
     */
    const setMode = useCallback((nextMode: NumberVisibilityMode) => {
        setModeState(nextMode);
        void saveCachedMode(nextMode);
    }, [saveCachedMode]);

    const value = useMemo<NumberVisibilityModeContextValue>(() => ({
        mode,
        setMode,
        loading,
    }), [loading, mode, setMode]);

    return (
        <NumberVisibilityModeContext.Provider value={value}>
            {children}
        </NumberVisibilityModeContext.Provider>
    );
};

/**
 * Accesses the dashboard number visibility mode. Falls back to compact when no provider is mounted.
 */
export const useNumberVisibilityMode = (): NumberVisibilityModeContextValue => {
    return useContext(NumberVisibilityModeContext) ?? defaultContextValue;
};

export default useNumberVisibilityMode;
